/**
 * ExoplanetTransitsPage - Main page component for exoplanet transit predictions.
 * Thin orchestration shell that delegates to hooks and components.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { calculateTargetAltitudes } from './utils/observabilityCalculations';
import { saveExoplanetPreferences } from './hooks/usePreferences';
import { useTransitSearch } from './hooks/useTransitSearch';
import { useObservatoryConfig } from './hooks/useObservatoryConfig';
import { useTransitSelection } from './hooks/useTransitSelection';
import TransitFilterPanel from './components/TransitFilterPanel';
import TransitResultsTable from './components/TransitResultsTable';
import TelescopeConfigDialog from './components/TelescopeConfigDialog';
import ObservationReviewDialog from './components/ObservationReviewDialog';
import { useStorageDestinations } from '../../components/storage/StorageDestinationPicker';
import { warningMessages } from '../../utils/darkTimeWarning';
import TransitDetailDialog from './components/TransitDetailDialog';
import { MOON_PEAK_DEFAULT, MOON_WIDTH_DEFAULT } from '../../constants/moon';

const ExoplanetTransitsPage = () => {
  const { user, updateUserPreferences } = useAuth();

  // Filter state
  const [filters, setFilters] = useState({
    hours: 24,
    minDepth: 0.5,
    maxVMag: 14,
    minAltitude: 30,
    enableMoonFilter: false,
    moonPeakSeparation: MOON_PEAK_DEFAULT,
    moonLorentzianWidth: MOON_WIDTH_DEFAULT,
  });
  const [showOnlyFullTransits, setShowOnlyFullTransits] = useState(false);

  // Baseline and altitude state
  const [baselineBefore, setBaselineBefore] = useState(60);
  const [baselineAfter, setBaselineAfter] = useState(60);
  const [minAltitude, setMinAltitude] = useState(30);

  // Dialog state
  const [configOpen, setConfigOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransitDetail, setSelectedTransitDetail] = useState(null);

  // Plan state
  const [observationPlans, setObservationPlans] = useState([]);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // #1119: per-transit "window runs past dark time" advisories from the batch create.
  const [darkTimeWarnings, setDarkTimeWarnings] = useState([]);

  // Observatory config hook
  const observatory = useObservatoryConfig({
    user,
    updateUserPreferences,
    setFilters,
    setBaselineBefore,
    setBaselineAfter,
    setMinAltitude,
    setShowOnlyFullTransits,
  });
  // External storage for the transits (#1474): the same choice the observation forms offer.
  const storage = useStorageDestinations(observatory.selectedProjectId);

  // Save preferences helper
  const doSavePreferences = useCallback(() => {
    saveExoplanetPreferences({
      filters,
      telescopeConfig: observatory.telescopeConfig,
      baselineBefore,
      baselineAfter,
      minAltitude,
      showOnlyFullTransits,
    });
  }, [filters, observatory.telescopeConfig, baselineBefore, baselineAfter, minAltitude, showOnlyFullTransits]);

  // Transit search hook
  const search = useTransitSearch({
    observatoryLocation: observatory.observatoryLocation,
    filters,
    onSavePreferences: doSavePreferences,
  });

  // Selection hook
  const selection = useTransitSelection({
    transits: search.transits,
    baselineBefore,
    baselineAfter,
  });

  // Debug baseline changes
  useEffect(() => {
    console.log('Baseline values updated:', { baselineBefore, baselineAfter });
  }, [baselineBefore, baselineAfter]);

  // Load data on mount
  useEffect(() => {
    observatory.loadObservatoryLocation();
    search.checkCacheStatus();
    observatory.loadProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update observation plans when baseline/altitude changes
  useEffect(() => {
    if (observationPlans.length === 0) return;

    const selectedObservatory = observatory.selectedObservatoryId
      ? observatory.observatories.find(o => o._id === observatory.selectedObservatoryId)
      : null;

    setObservationPlans(prevPlans =>
      prevPlans.map(plan => recalculatePlanTimes(plan, selectedObservatory, baselineBefore, baselineAfter, minAltitude))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineBefore, baselineAfter, minAltitude, observationPlans.length, observatory.selectedObservatoryId]);

  const showTransitDetails = (transit) => {
    setSelectedTransitDetail(transit);
    setDetailOpen(true);
  };

  const handleSaveTelescopeConfig = async () => {
    const success = await observatory.saveTelescopeConfig({
      onSavePreferences: doSavePreferences,
      setError: search.setError,
    });
    if (success) setConfigOpen(false);
  };

  const generateObservationPlan = async () => {
    if (selection.selectedTransits.size === 0) {
      search.setError({ type: 'warning', message: 'Please select at least one transit to observe' });
      return;
    }

    setGeneratingPlan(true);
    search.setError(null);

    try {
      const transitIdsArray = Array.from(selection.selectedTransits);
      const selectedTransitData = buildSelectedTransitData(transitIdsArray, search.transits);

      const requestData = {
        transit_ids: transitIdsArray,
        transit_data: selectedTransitData,
        telescope_config: {
          ...observatory.telescopeConfig,
          observatoryLocation: observatory.observatoryLocation,
          baselineBefore,
          baselineAfter,
        },
      };

      const response = await apiService.generateExoplanetPlan(requestData);
      const plans = response.data.observation_plans;

      const selectedObs = observatory.selectedObservatoryId
        ? observatory.observatories.find(o => o._id === observatory.selectedObservatoryId)
        : null;

      const enrichedPlans = plans.map(plan =>
        enrichPlan(plan, selectedTransitData, selectedObs, baselineBefore, baselineAfter, minAltitude)
      );

      setObservationPlans(enrichedPlans);
      setReviewOpen(true);
      search.setError({
        type: 'info',
        message: `Generated ${plans.length} observation plan${plans.length > 1 ? 's' : ''}. Please review before submitting.`
      });
    } catch (error) {
      console.error('Failed to generate observation plan:', error);
      const errorMessage = extractErrorMessage(error);
      search.setError({ type: 'error', message: errorMessage });
    } finally {
      setGeneratingPlan(false);
    }
  };

  const submitObservations = async () => {
    if (!observatory.selectedProjectId) {
      setSubmitError('Please select a project for the observations');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setDarkTimeWarnings([]);

    try {
      let successCount = 0;
      let failedCount = 0;
      const errors = [];
      const collectedDarkTimeWarnings = [];  // #1119, one per transit whose window overruns dark time

      for (const plan of observationPlans) {
        try {
          const observation = buildObservationPayload(
            plan, observatory.selectedProjectId, observatory.selectedObservatoryId,
            minAltitude, baselineBefore, baselineAfter, filters
          );
          observation.external_storage_destinations = storage.selectedIds;

          console.log('Sending observation to API:', {
            name: observation.name,
            observation_type: observation.observation_type,
            fixed_time_start: observation.fixed_time_start,
            fixed_time_end: observation.fixed_time_end,
          });
          const created = await apiService.createObservation(observation);
          successCount++;
          // #1119: a transit window comes from an ephemeris and routinely butts up against or
          // crosses twilight, so this is the case the dark-time advisory exists for. Collect them
          // per-target: a batch that silently truncated three of eight transits is exactly the
          // invisible failure this fixes.
          for (const msg of warningMessages(created)) {
            collectedDarkTimeWarnings.push(`${plan.target.name}: ${msg}`);
          }
        } catch (err) {
          console.error(`Failed to create observation for ${plan.target.name}:`, err);
          const serverMsg = err.response?.data?.error?.message || err.response?.data?.error || err.message;
          const statusCode = err.response?.status;
          errors.push(`${plan.target.name}: ${statusCode ? `[${statusCode}] ` : ''}${serverMsg}`);
          failedCount++;
        }
      }

      if (successCount > 0) {
        search.setError({
          type: 'success',
          message: `Successfully created ${successCount} observation${successCount > 1 ? 's' : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}`
        });
        if (failedCount > 0) {
          setSubmitError(`${failedCount} failed:\n${errors.join('\n')}`);
        }
        // #1119: when any window runs past dark time, keep the review panel open and let the user
        // dismiss it. The 2.5s auto-clear below would wipe the advisory before it could be read.
        if (collectedDarkTimeWarnings.length > 0) {
          setDarkTimeWarnings(collectedDarkTimeWarnings);
          return;
        }
        setTimeout(() => {
          selection.setSelectedTransits(new Set());
          setObservationPlans([]);
          setReviewOpen(false);
          setSubmitError(null);
        }, 2500);
      } else {
        setSubmitError(`Failed to create observations:\n${errors.join('\n')}`);
      }
    } catch (error) {
      console.error('Failed to submit observations:', error);
      setSubmitError(`Failed to submit: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 3, mb: 3 }}>
        <PageHeader
          onConfigOpen={() => setConfigOpen(true)}
          onUpdateCache={search.updateCache}
        />

        <CacheStatusAlert
          cacheStatus={search.cacheStatus}
          onUpdateCache={search.updateCache}
          onClearAndRefresh={search.clearAndRefreshCache}
        />

        <TransitFilterPanel
          filters={filters}
          setFilters={setFilters}
          showOnlyFullTransits={showOnlyFullTransits}
          setShowOnlyFullTransits={setShowOnlyFullTransits}
          selectedObservatoryId={observatory.selectedObservatoryId}
          observatories={observatory.observatories}
          handleObservatoryChange={observatory.handleObservatoryChange}
          projects={observatory.projects}
          selectedProjectId={observatory.selectedProjectId}
          setSelectedProjectId={observatory.setSelectedProjectId}
          loading={search.loading}
          onSearch={search.searchTransits}
        />

        {search.error && (
          <Alert severity={search.error.type} sx={{ mb: 2 }} onClose={() => search.setError(null)}>
            {typeof search.error.message === 'string' ? search.error.message : JSON.stringify(search.error.message)}
          </Alert>
        )}

        <TransitResultsTable
          transits={search.transits}
          selectedTransits={selection.selectedTransits}
          handleSelectAll={selection.handleSelectAll}
          handleSelectTransit={selection.handleSelectTransit}
          overlapMap={selection.overlapMap}
          observatories={observatory.observatories}
          selectedObservatoryId={observatory.selectedObservatoryId}
          showOnlyFullTransits={showOnlyFullTransits}
          baselineBefore={baselineBefore}
          baselineAfter={baselineAfter}
          filters={filters}
          generatingPlan={generatingPlan}
          onGeneratePlan={generateObservationPlan}
          onShowDetails={showTransitDetails}
        />
      </Paper>

      <TelescopeConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        telescopeConfig={observatory.telescopeConfig}
        setTelescopeConfig={observatory.setTelescopeConfig}
        onSave={handleSaveTelescopeConfig}
      />

      <ObservationReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        observationPlans={observationPlans}
        setObservationPlans={setObservationPlans}
        baselineBefore={baselineBefore}
        baselineAfter={baselineAfter}
        setBaselineBefore={setBaselineBefore}
        setBaselineAfter={setBaselineAfter}
        minAltitude={minAltitude}
        setMinAltitude={setMinAltitude}
        selectedObservatoryId={observatory.selectedObservatoryId}
        observatories={observatory.observatories}
        handleObservatoryChange={observatory.handleObservatoryChange}
        projects={observatory.projects}
        selectedProjectId={observatory.selectedProjectId}
        setSelectedProjectId={observatory.setSelectedProjectId}
        storage={storage}
        telescopeConfig={observatory.telescopeConfig}
        observatoryLocation={observatory.observatoryLocation}
        selectedTransits={selection.selectedTransits}
        setSelectedTransits={selection.setSelectedTransits}
        transits={search.transits}
        submitting={submitting}
        submitError={submitError}
        setSubmitError={setSubmitError}
        darkTimeWarnings={darkTimeWarnings}
        onDismissDarkTimeWarnings={() => {
          setDarkTimeWarnings([]);
          selection.setSelectedTransits(new Set());
          setObservationPlans([]);
          setReviewOpen(false);
          setSubmitError(null);
        }}
        onSubmit={submitObservations}
        onRegenerate={generateObservationPlan}
      />

      <TransitDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        transit={selectedTransitDetail}
        observatories={observatory.observatories}
        observationPlans={observationPlans}
        baselineBefore={baselineBefore}
        baselineAfter={baselineAfter}
        filters={filters}
      />
    </Container>
  );
};

// --- Helper sub-components ---

const PageHeader = ({ onConfigOpen, onUpdateCache }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
    <Box display="flex" alignItems="center">
      <PublicIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
      <div>
        <Typography variant="h4">Exoplanet Transit Predictions</Typography>
        <Typography variant="body2" color="text.secondary">
          Search for upcoming exoplanet transits observable from your location
        </Typography>
      </div>
    </Box>
    <Box>
      <Tooltip title="Configure telescope">
        <IconButton onClick={onConfigOpen} color="primary">
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Update NASA cache">
        <IconButton onClick={onUpdateCache} color="primary">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

const CacheStatusAlert = ({ cacheStatus, onUpdateCache, onClearAndRefresh }) => {
  if (!cacheStatus) return null;

  return (
    <Alert
      severity={cacheStatus.is_refreshing ? 'info' : cacheStatus.needs_update ? 'warning' : 'info'}
      sx={{ mb: 2 }}
      icon={cacheStatus.is_refreshing ? <CircularProgress size={20} /> : undefined}
      action={
        !cacheStatus.is_refreshing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {cacheStatus.needs_update && (
              <Button color="inherit" size="small" onClick={onUpdateCache}>
                Update Now
              </Button>
            )}
            <Button
              color="inherit"
              size="small"
              onClick={onClearAndRefresh}
              startIcon={<RefreshIcon />}
            >
              Clear & Refresh
            </Button>
          </Box>
        )
      }
    >
      {cacheStatus.is_refreshing
        ? 'Exoplanet cache is refreshing -- this may take 1-2 minutes. Transit search will be available once complete.'
        : <>
            Cache contains {cacheStatus.total_exoplanets} exoplanets.
            Last updated: {cacheStatus.last_updated ? new Date(cacheStatus.last_updated).toISOString().replace('T', ' ').replace('.000Z', ' UTC') : 'Never'}.
            {cacheStatus.needs_update && ' Cache is outdated and should be updated.'}
          </>
      }
    </Alert>
  );
};

// --- Pure helper functions ---

function buildSelectedTransitData(transitIdsArray, transits) {
  return transitIdsArray.map(transitId => {
    const transit = transits.find(t =>
      `${t.exoplanet.name}_${new Date(t.transit.time_utc).getTime()}` === transitId
    );
    if (transit) {
      return { id: transitId, exoplanet: transit.exoplanet, transit: transit.transit };
    }
    return null;
  }).filter(t => t !== null);
}

function enrichPlan(plan, selectedTransitData, selectedObservatory, baselineBefore, baselineAfter, minAltitude) {
  const transitData = selectedTransitData.find(t => t.exoplanet.name === plan.target?.name);

  let observationStartTime, observationEndTime;
  const transitDurationHours = plan.expected_metrics?.transit_duration_hours || 2;

  if (plan.timing?.transit_ingress && plan.timing?.transit_egress) {
    const ingressTime = new Date(plan.timing.transit_ingress);
    const egressTime = new Date(plan.timing.transit_egress);
    observationStartTime = new Date(ingressTime.getTime() - (baselineBefore * 60 * 1000));
    observationEndTime = new Date(egressTime.getTime() + (baselineAfter * 60 * 1000));
  } else if (plan.timing?.transit_midpoint && transitDurationHours) {
    const midpointTime = new Date(plan.timing.transit_midpoint);
    const halfTransitMs = (transitDurationHours * 60 * 60 * 1000) / 2;
    const ingressTime = new Date(midpointTime.getTime() - halfTransitMs);
    const egressTime = new Date(midpointTime.getTime() + halfTransitMs);
    observationStartTime = new Date(ingressTime.getTime() - (baselineBefore * 60 * 1000));
    observationEndTime = new Date(egressTime.getTime() + (baselineAfter * 60 * 1000));
  } else {
    observationStartTime = plan.timing?.start_time ? new Date(plan.timing.start_time) : null;
    observationEndTime = plan.timing?.end_time ? new Date(plan.timing.end_time) : null;
  }

  if (transitData && selectedObservatory && observationStartTime && observationEndTime) {
    try {
      const altitudes = calculateTargetAltitudes(
        transitData, selectedObservatory, observationStartTime, observationEndTime, minAltitude
      );
      if (altitudes) {
        observationStartTime = altitudes.observationStart.time;
        observationEndTime = altitudes.observationEnd.time;
      }
    } catch (err) {
      console.warn('Altitude adjustment failed during enrichment:', err);
    }
  }

  const totalDurationHours = (observationStartTime && observationEndTime)
    ? (observationEndTime.getTime() - observationStartTime.getTime()) / (1000 * 60 * 60)
    : plan.expected_metrics?.total_duration_hours || 2;

  return {
    ...plan,
    transitData: transitData || null,
    observationStartTime,
    observationEndTime,
    totalDurationHours,
    currentBaselineBefore: baselineBefore,
    currentBaselineAfter: baselineAfter,
  };
}

function recalculatePlanTimes(plan, selectedObservatory, baselineBefore, baselineAfter, minAltitude) {
  try {
    let observationStartTime, observationEndTime;
    const transitDurationHours = plan.expected_metrics?.transit_duration_hours || 2;

    if (plan.timing?.transit_ingress && plan.timing?.transit_egress) {
      const ingressTime = new Date(plan.timing.transit_ingress);
      const egressTime = new Date(plan.timing.transit_egress);
      observationStartTime = new Date(ingressTime.getTime() - (baselineBefore * 60 * 1000));
      observationEndTime = new Date(egressTime.getTime() + (baselineAfter * 60 * 1000));
    } else if (plan.timing?.transit_midpoint && transitDurationHours) {
      const midpointTime = new Date(plan.timing.transit_midpoint);
      const halfTransitMs = (transitDurationHours * 60 * 60 * 1000) / 2;
      const ingressTime = new Date(midpointTime.getTime() - halfTransitMs);
      const egressTime = new Date(midpointTime.getTime() + halfTransitMs);
      observationStartTime = new Date(ingressTime.getTime() - (baselineBefore * 60 * 1000));
      observationEndTime = new Date(egressTime.getTime() + (baselineAfter * 60 * 1000));
    } else {
      observationStartTime = plan.timing?.start_time ? new Date(plan.timing.start_time) : null;
      observationEndTime = plan.timing?.end_time ? new Date(plan.timing.end_time) : null;
    }

    if (plan.transitData && selectedObservatory && observationStartTime && observationEndTime) {
      try {
        const altitudes = calculateTargetAltitudes(
          plan.transitData, selectedObservatory, observationStartTime, observationEndTime, minAltitude
        );
        if (altitudes) {
          observationStartTime = altitudes.observationStart.time;
          observationEndTime = altitudes.observationEnd.time;
        }
      } catch (altError) {
        console.warn('Altitude adjustment failed, keeping baseline times:', altError);
      }
    }

    const totalDurationHours = (observationStartTime && observationEndTime)
      ? (observationEndTime.getTime() - observationStartTime.getTime()) / (1000 * 60 * 60)
      : plan.totalDurationHours || plan.expected_metrics?.total_duration_hours || 2;

    return {
      ...plan,
      observationStartTime,
      observationEndTime,
      totalDurationHours,
      currentBaselineBefore: baselineBefore,
      currentBaselineAfter: baselineAfter,
    };
  } catch (err) {
    console.error('Error recalculating plan times for', plan.target?.name, err);
    return plan;
  }
}

function buildObservationPayload(plan, projectId, observatoryId, minAltitude, baselineBefore, baselineAfter, filters) {
  const observationStartTime = plan.observationStartTime;
  const observationEndTime = plan.observationEndTime;
  const totalDurationHours = plan.totalDurationHours;

  return {
    name: `Transit: ${plan.target.name}`,
    description: `Exoplanet transit observation. V=${plan.target.vmag?.toFixed(1)}, Depth=${plan.expected_metrics?.transit_depth?.toFixed(2)}%`,
    project_id: projectId,
    assigned_to: observatoryId,
    type: 'science',
    observation_type: 'fixed_time',
    // Transits must fill the whole window. Since #928 (SUP-2026-0005) the server no
    // longer force-fills fixed_time observations, so fill_time must be set explicitly
    // or the plugin takes exactly exposure_count frames (=1) and stops after one image.
    fill_time: true,
    fixed_time_start: observationStartTime ? new Date(observationStartTime).toISOString() : null,
    fixed_time_end: observationEndTime ? new Date(observationEndTime).toISOString() : null,
    transit_ingress: plan.timing?.transit_ingress ? new Date(plan.timing.transit_ingress).toISOString() : null,
    transit_egress: plan.timing?.transit_egress ? new Date(plan.timing.transit_egress).toISOString() : null,
    targets: [{
      type: 'exoplanet',
      id: `exo_${plan.target.name.replace(/\s+/g, '_')}`,
      name: plan.target.name,
      ra: plan.target.ra,
      dec: plan.target.dec,
      exposure_configs: [{
        filter: plan.exposure_plan?.filter || 'Clear',
        exposure_time: plan.exposure_plan?.exposure_time || 120,
        exposure_count: 0, // Unlimited: with fill_time=true the plugin loops until fixed_time_end
        binning: plan.exposure_plan?.binning || 1,
        gain: null,
        offset: null,
      }],
    }],
    priority: plan.priority || 5,
    estimated_duration_minutes: Math.round(totalDurationHours * 60),
    constraints: {
      minimum_altitude: minAltitude,
      is_time_critical: true,
      baseline_before: baselineBefore || 60,
      baseline_after: baselineAfter || 60,
      // #1157: the search filter and the scheduler now speak the same two parameters, so the
      // peak and width flow straight through instead of being re-derived from an anchor day.
      ...(filters.enableMoonFilter ? {
        moon_separation: filters.moonPeakSeparation,
        moon_avoidance_width: filters.moonLorentzianWidth,
      } : {}),
    },
    metadata: {
      observation_type: 'exoplanet_transit',
      transit_midpoint: plan.timing?.transit_midpoint,
      transit_duration_hours: plan.expected_metrics?.transit_duration_hours,
      transit_depth: plan.expected_metrics?.transit_depth,
      expected_snr: plan.expected_metrics?.expected_snr,
      estimated_exposures: plan.exposure_plan?.num_exposures,
    },
  };
}

function extractErrorMessage(error) {
  if (error.response?.data) {
    if (typeof error.response.data === 'string') return error.response.data;
    if (error.response.data.error) return error.response.data.error;
    if (error.response.data.message) return error.response.data.message;
  }
  return error.message || 'Failed to generate observation plan';
}

export default ExoplanetTransitsPage;
